import React, { useState } from "react";
import { Box } from "../../../design-system";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { colors } from "../../../design-system/colors";
import { CURSOR_POINTER, CURSOR_DEFAULT } from "../constants/myLibraryText";
import cherryLogo from "../../../../assets/brand/logo_site.svg";

interface VideoThumbnailProps {
  videoUrl: string;
  /** Pre-generated thumbnail JPEG. Shown as a plain <img> so the browser never has to
   *  decode a video frame (browsers cap concurrent video decodes ~4-6, which makes
   *  decoded-frame thumbnails go blank when several are on screen). */
  thumbnailUrl?: string;
  onClick?: () => void;
  /** Render a plain black box instead of the video (for non-video file types) */
  blackBackground?: boolean;
  /** When provided, shows a copy icon overlay on the black background */
  onCopyToClipboard?: () => void;
  /** Override the thumbnail height in px (default 85) — used by the size slider. */
  heightPx?: number;
  /** When there's no usable image, show the cherry logo on a dark tile instead of a
   *  (often black) <video> frame — for audio-only uploads or missing thumbnails. */
  cherryFallback?: boolean;
}

/**
 * Video Thumbnail Component
 * Displays video thumbnail with hover play button overlay
 */
const VideoThumbnail: React.FC<VideoThumbnailProps> = ({
  videoUrl,
  thumbnailUrl,
  onClick,
  blackBackground = false,
  onCopyToClipboard,
  heightPx,
  cherryFallback = false,
}): JSX.Element => {
  const [isHovered, setIsHovered] = useState<boolean>(false);
  const [imgFailed, setImgFailed] = useState<boolean>(false);
  const showCopyOverlay = blackBackground && !!onCopyToClipboard;
  // Prefer the pre-made JPEG; only fall back to decoding the video if there's no image.
  const useImage = !blackBackground && !!thumbnailUrl && !imgFailed;
  // No image to show (audio-only / missing thumbnail) → cherry logo instead of a black box.
  const showCherry = cherryFallback && !useImage && !blackBackground;

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        height: heightPx ? `${heightPx}px` : "85px",
        borderRadius: "8px",
        overflow: "hidden",
        backgroundColor: blackBackground ? colors.other.black : undefined,
        cursor: onClick && !blackBackground ? CURSOR_POINTER : CURSOR_DEFAULT,
      }}
      onMouseEnter={() => !blackBackground && setIsHovered(true)}
      onMouseLeave={() => !blackBackground && setIsHovered(false)}
      onClick={blackBackground ? undefined : onClick}
    >
      {useImage && (
        <img
          src={thumbnailUrl}
          alt=""
          onError={() => setImgFailed(true)}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "contain",
            backgroundColor: "#000",
            display: "block",
          }}
        />
      )}
      {!blackBackground && !useImage && !showCherry && (
        <video
          src={videoUrl}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "contain",
            backgroundColor: "#000",
          }}
          preload="metadata"
        />
      )}
      {showCherry && (
        <Box
          sx={{
            width: "100%",
            height: "100%",
            backgroundColor: colors.other.black,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <img src={cherryLogo} alt="" style={{ height: "55%", width: "auto", opacity: 0.85 }} />
        </Box>
      )}
      {onClick && !blackBackground && (
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "32px",
            height: "32px",
            borderRadius: "50%",
            backgroundColor: colors.overlay.videoPlay,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "opacity 0.2s",
            opacity: isHovered ? 1 : 0,
            cursor: CURSOR_POINTER,
            "&:hover": {
              backgroundColor: colors.overlay.videoPlayActive,
            },
          }}
        >
          <PlayArrowIcon
            sx={{
              fontSize: "18px",
              color: colors.other.black,
              marginLeft: "2px",
            }}
          />
        </Box>
      )}
      {showCopyOverlay && (
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "32px",
            height: "32px",
            borderRadius: "50%",
            backgroundColor: colors.overlay.videoPlay,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: CURSOR_POINTER,
            "&:hover": {
              backgroundColor: colors.overlay.videoPlayActive,
            },
          }}
          onClick={(e) => { e.stopPropagation(); onCopyToClipboard(); }}
        >
          <ContentCopyIcon
            sx={{
              fontSize: "16px",
              color: colors.other.black,
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default VideoThumbnail;
